/**
 * SEO helpers — JSON-LD structured data generators.
 * These return strings ready to be embedded as a `scripts[].children` value
 * in a TanStack Router `head()` definition.
 */

export const SITE_URL = "https://kakaiys.com";
export const SITE_NAME = "KAKAIYS";

/** Build a canonical URL for a given path (e.g. "/shop"). */
export function canonical(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${p}`;
}

/** Standard OG/Twitter meta block for a page. */
export function socialMeta(input: {
  title: string;
  description: string;
  image?: string;
  url: string;
  type?: "website" | "article" | "product";
}) {
  const meta: Array<{ name?: string; property?: string; content: string }> = [
    { property: "og:title", content: input.title },
    { property: "og:description", content: input.description },
    { property: "og:url", content: input.url },
    { property: "og:type", content: input.type ?? "website" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: input.title },
    { name: "twitter:description", content: input.description },
  ];
  if (input.image) {
    meta.push({ property: "og:image", content: input.image });
    meta.push({ name: "twitter:image", content: input.image });
  }
  return meta;
}

export type BlogPostingSchemaInput = {
  slug: string;
  title: string;
  description: string;
  image: string;
  datePublished?: string;
  author?: string;
  keywords?: string[];
};

export function articleJsonLd(input: BlogPostingSchemaInput): string {
  const url = `${SITE_URL}/journal/${input.slug}`;
  const image = input.image.startsWith("http") ? input.image : `${SITE_URL}${input.image}`;
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: input.title,
    description: input.description,
    image: [image],
    datePublished: input.datePublished,
    author: { "@type": "Organization", name: input.author ?? SITE_NAME },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.ico` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    keywords: (input.keywords ?? []).join(", "),
  });
}

export type ProductSchemaInput = {
  slug: string;
  name: string;
  description: string;
  image: string;
  price: number;
  currency?: string;
  brand?: string;
  averageRating?: number;
  reviewCount?: number;
};

export function productJsonLd(input: ProductSchemaInput): string {
  const url = `${SITE_URL}/product/${input.slug}`;
  const image = input.image.startsWith("http") ? input.image : `${SITE_URL}${input.image}`;
  const obj: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: input.name,
    description: input.description,
    image: [image],
    sku: input.slug,
    brand: { "@type": "Brand", name: input.brand ?? SITE_NAME },
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: input.currency ?? "USD",
      price: input.price.toFixed(2),
      availability: "https://schema.org/InStock",
    },
  };
  if (input.averageRating && input.reviewCount && input.reviewCount > 0) {
    obj.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: input.averageRating.toFixed(1),
      reviewCount: input.reviewCount,
    };
  }
  return JSON.stringify(obj);
}

export type BreadcrumbItem = { name: string; url: string };
export function breadcrumbJsonLd(items: BreadcrumbItem[]): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
    })),
  });
}

export type FAQItem = { question: string; answer: string };
export function faqPageJsonLd(items: FAQItem[]): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  });
}

export type HowToStep = { name: string; text: string; image?: string };
export type HowToSchemaInput = {
  name: string;
  description: string;
  totalTime?: string; // ISO 8601 duration e.g. "PT30M"
  steps: HowToStep[];
  image?: string;
};
export function howToJsonLd(input: HowToSchemaInput): string {
  const obj: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: input.name,
    description: input.description,
    step: input.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      ...(s.image ? { image: s.image.startsWith("http") ? s.image : `${SITE_URL}${s.image}` } : {}),
    })),
  };
  if (input.totalTime) obj.totalTime = input.totalTime;
  if (input.image) obj.image = input.image.startsWith("http") ? input.image : `${SITE_URL}${input.image}`;
  return JSON.stringify(obj);
}

export function organizationJsonLd(): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": ["Organization", "Brand"],
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    legalName: "KAKAIYS Maison",
    alternateName: ["Kakaiys", "Kakaiys Leather", "KAKAIYS Maison"],
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      "@id": `${SITE_URL}/#logo`,
      url: `${SITE_URL}/favicon-512.png`,
      contentUrl: `${SITE_URL}/favicon-512.png`,
      width: 512,
      height: 512,
      caption: SITE_NAME,
    },
    image: `${SITE_URL}/favicon-512.png`,
    slogan: "Handcrafted leather, built for generations.",
    description:
      "KAKAIYS is a quiet maison of leather based in Sialkot, Pakistan. Hand-finished jackets, bags, wallets and belts, made one at a time using full-grain and vegetable-tanned leathers.",
    foundingLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Sialkot",
        addressCountry: "PK",
      },
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: "Sialkot",
      addressCountry: "PK",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "support@kakaiys.com",
        availableLanguage: ["English"],
        areaServed: ["US", "GB", "EU", "CA", "AU", "Worldwide"],
      },
    ],
    knowsAbout: [
      "Leather craftsmanship",
      "Full-grain leather",
      "Vegetable-tanned leather",
      "Leather jackets",
      "Hand-stitched leather",
      "Leather care and maintenance",
      "Heritage leather goods",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: `${SITE_NAME} Collection`,
      itemListElement: [
        { "@type": "OfferCatalog", name: "Leather jackets" },
        { "@type": "OfferCatalog", name: "Leather bags" },
        { "@type": "OfferCatalog", name: "Leather wallets" },
        { "@type": "OfferCatalog", name: "Leather belts" },
      ],
    },
    sameAs: [
      "https://www.instagram.com/kakaiys",
      "https://www.facebook.com/kakaiys",
      "https://www.pinterest.com/kakaiys",
    ],
  });
}

export function websiteJsonLd(): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    description:
      "KAKAIYS — a quiet maison of hand-finished leather. Jackets, bags, wallets and accessories, made one at a time in Sialkot.",
    publisher: { "@id": `${SITE_URL}/#organization` },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/shop?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
    inLanguage: "en",
  });
}

/**
 * Speakable schema — tells voice assistants (Google Assistant, Alexa) which
 * parts of the page to read aloud. Pass CSS selectors for the speakable bits.
 */
export function speakableJsonLd(cssSelectors: string[]): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "WebPage",
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: cssSelectors,
    },
  });
}

/**
 * Generic Article-style "About" schema for AI assistants — concise key/value
 * facts that LLMs can quote verbatim ("KAKAIYS is …, founded in …").
 */
export function collectionPageJsonLd(input: {
  name: string;
  description: string;
  url: string;
  itemCount?: number;
}): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: input.name,
    description: input.description,
    url: input.url,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    publisher: { "@id": `${SITE_URL}/#organization` },
    ...(input.itemCount !== undefined
      ? {
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: input.itemCount,
          },
        }
      : {}),
  });
}

export function localBusinessJsonLd(): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Store",
    "@id": `${SITE_URL}/#atelier`,
    name: `${SITE_NAME} Atelier`,
    description:
      "The KAKAIYS atelier in Sialkot, Pakistan — where every leather piece is cut, stitched and finished by hand. Visits by appointment only.",
    url: `${SITE_URL}/contact`,
    image: `${SITE_URL}/favicon-512.png`,
    telephone: "",
    email: "care@kakaiys.com",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Sialkot",
      addressRegion: "Punjab",
      addressCountry: "PK",
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "09:00",
        closes: "19:00",
      },
    ],
    parentOrganization: { "@id": `${SITE_URL}/#organization` },
    priceRange: "$$$",
  });
}

export function brandAggregateRatingJsonLd(input: {
  ratingValue: number;
  reviewCount: number;
}): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Brand",
    "@id": `${SITE_URL}/#brand-rating`,
    name: SITE_NAME,
    url: SITE_URL,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: input.ratingValue.toFixed(1),
      bestRating: "5",
      worstRating: "1",
      reviewCount: input.reviewCount,
    },
  });
}

export function aboutPageJsonLd(input: {
  name: string;
  description: string;
  url: string;
  topics: string[];
}): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: input.name,
    description: input.description,
    url: input.url,
    about: input.topics.map((t) => ({ "@type": "Thing", name: t })),
    isPartOf: { "@id": `${SITE_URL}/#website` },
    publisher: { "@id": `${SITE_URL}/#organization` },
  });
}
